import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    Snackbar,
    Chip,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Link,
    Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { format, parseISO, addDays } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import {
    Check as CheckIcon,
    Close as CloseIcon,
    Done as DoneIcon,
    MoreVert as MoreVertIcon,
    Visibility,
    OpenInNew
} from '@mui/icons-material';
import { appointmentService } from '../../services/appointmentService';
import { spreadsService } from '../../services/spreadsService';
import { Appointment } from '../../types/appointment';
import { AppointmentDetailsDialog } from '../../components/admin/AppointmentDetailsDialog';
import { collection, query, orderBy, where, limit, startAfter, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { errorHandler } from '../../utils/errorHandler';
import { ConfirmButton, RejectButton, CompleteButton, ViewButton } from '../../styles/buttons';

interface Filters {
    status: string;
    startDate: Date | null;
    endDate: Date | null;
    paymentType: string;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
}

interface SpreadInfo {
    id: string;
    title: string;
    points: number;
}

const AppointmentAdmin: React.FC = () => {
    const today = new Date();
    const threeDaysLater = addDays(today, 3);

    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [totalAppointments, setTotalAppointments] = useState(0);
    const [loading, setLoading] = useState(true);
    const [collectionExists, setCollectionExists] = useState(true);
    const [spreads, setSpreads] = useState<Map<string, SpreadInfo>>(new Map());
    const [filters, setFilters] = useState<Filters>({
        status: 'all',
        startDate: today,
        endDate: threeDaysLater,
        paymentType: 'all',
        sortBy: 'date',
        sortDirection: 'desc'
    });
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [lastVisible, setLastVisible] = useState<any>(null);

    // Effect Hooks
    useEffect(() => {
        checkCollection();
        loadSpreads();
    }, []);

    const loadSpreads = async () => {
        try {
            const spreadsData = await spreadsService.getAll();
            const spreadsMap = new Map<string, SpreadInfo>();
            spreadsData.forEach(spread => {
                spreadsMap.set(spread.id, {
                    id: spread.id,
                    title: spread.title,
                    points: spread.points || 0
                });
            });
            setSpreads(spreadsMap);
        } catch (error) {
            errorHandler.handleError(error, 'Error loading spreads');
        }
    };

    const checkCollection = async () => {
        try {
            setLoading(true);
            const exists = await appointmentService.checkCollectionExists();
            setCollectionExists(exists);
            if (exists) {
                await loadAppointments();
            } else {
                setLoading(false);
            }
        } catch (error) {
            errorHandler.handleError(error, 'Error checking collection');
            showSnackbar('Randevular kontrol edilirken bir hata oluştu', 'error');
            setLoading(false);
        }
    };

    // API işlemleri
    const loadAppointments = async () => {
        try {
            setLoading(true);
            const appointmentsRef = collection(db, 'appointments');

            // Başlangıç tarihini yerel saat dilimine göre ayarla
            const startDate = new Date(filters.startDate!);
            startDate.setHours(0, 0, 0, 0);
            // Tarihi YYYY-MM-DD formatına çevir ve yerel saat diliminde tut
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');

            // Bitiş tarihini yerel saat dilimine göre ayarla
            const endDate = new Date(filters.endDate!);
            endDate.setHours(23, 59, 59, 999);
            // Tarihi YYYY-MM-DD formatına çevir ve yerel saat diliminde tut
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            // Sadece tarih aralığına göre sorgu yap
            let q = query(
                appointmentsRef,
                where('date', '>=', formattedStartDate),
                where('date', '<=', formattedEndDate),
                orderBy('date', filters.sortDirection)
            );

            const querySnapshot = await getDocs(q);
            const appointmentData: Appointment[] = [];
            
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                // Randevu tarihini kontrol et
                const appointmentDate = data.date;
                
                // Eğer randevu tarihi seçilen başlangıç tarihinden küçükse veya
                // seçilen bitiş tarihinden büyükse, bu randevuyu dahil etme
                if (appointmentDate < formattedStartDate || appointmentDate > formattedEndDate) {
                    return;
                }

                appointmentData.push({
                    id: doc.id,
                    userId: data.userId || '',
                    userName: data.name || '',
                    userEmail: data.email || '',
                    userPhone: data.phone || '',
                    date: data.date || '',
                    time: data.time || '',
                    status: data.status || 'pending',
                    paymentType: data.paymentType || 'regular',
                    paymentStatus: data.paymentStatus || 'waiting_confirmation',
                    notes: data.notes || '',
                    receiptUrl: data.receiptUrl || '',
                    receiptFileId: data.receiptFileId || '',
                    spreadId: data.spreadId || '',
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    rejectionReason: data.rejectionReason || ''
                });
            });

            // Tarihe göre sırala
            appointmentData.sort((a, b) => {
                const dateA = `${a.date} ${a.time}`;
                const dateB = `${b.date} ${b.time}`;
                return filters.sortDirection === 'asc' 
                    ? dateA.localeCompare(dateB)
                    : dateB.localeCompare(dateA);
            });

            setAppointments(appointmentData);
        } catch (error) {
            errorHandler.handleError(error, 'Error loading appointments');
            showSnackbar('Randevular yüklenirken bir hata oluştu', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAppointments();
    }, [filters.startDate, filters.endDate, filters.sortDirection]);

    const handlePageChange = (event: unknown, newPage: number) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setFilters(prev => ({
            ...prev,
            pageSize: newPageSize,
            page: 0
        }));
        setLastVisible(null); // Sayfa boyutu değiştiğinde cursor'ı sıfırla
    };

    const handleConfirm = async (id: string) => {
        try {
            await appointmentService.updateStatus(id, 'confirmed');
            showSnackbar('Randevu başarıyla onaylandı', 'success');
            await loadAppointments();
        } catch (error) {
            errorHandler.handleError(error, 'Error confirming appointment');
            showSnackbar('Randevu onaylanırken bir hata oluştu', 'error');
        }
    };

    const handleReject = async () => {
        if (!selectedAppointmentId || !rejectionReason.trim()) return;

        try {
            await appointmentService.updateStatus(selectedAppointmentId, 'rejected', rejectionReason.trim());
            showSnackbar('Randevu başarıyla reddedildi', 'success');
            await loadAppointments();
            setRejectDialogOpen(false);
            setRejectionReason('');
            setSelectedAppointmentId(null);
        } catch (error) {
            errorHandler.handleError(error, 'Error rejecting appointment');
            showSnackbar('Randevu reddedilirken bir hata oluştu', 'error');
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>, appointment: Appointment) => {
        setAnchorEl(event.currentTarget);
        setSelectedAppointment(appointment);
    };

    const handleStatusMenuClose = () => {
        setAnchorEl(null);
        setSelectedAppointment(null);
    };

    const handleStatusChange = async (newStatus: Appointment['status'], appointmentId?: string) => {
        try {
            const id = appointmentId || selectedAppointment?.id;
            if (!id) return;

            if (newStatus === 'cancelled') {
                setSelectedAppointmentId(id);
                setRejectDialogOpen(true);
            } else {
                await appointmentService.updateStatus(id, newStatus);
                showSnackbar(`Randevu durumu "${getStatusText(newStatus)}" olarak güncellendi`, 'success');
                await loadAppointments();
            }
        } catch (error) {
            errorHandler.handleError(error, 'Error updating appointment status');
            showSnackbar('Durum güncellenirken bir hata oluştu', 'error');
        }
        handleStatusMenuClose();
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return 'Ödeme Bekliyor';
            case 'confirmed': return 'Onaylandı';
            case 'completed': return 'Tamamlandı';
            case 'rejected': return 'Reddedildi';
            default: return 'Bilinmiyor';
        }
    };

    const getStatusChipColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
        switch (status) {
            case 'pending': return 'warning';
            case 'confirmed': return 'info';
            case 'completed': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    const filteredAppointments = useMemo(() => {
        return appointments.filter(appointment => {
            // Status filtresi
            if (filters.status !== 'all' && appointment.status !== filters.status) {
                return false;
            }

            // Ödeme tipi filtresi
            if (filters.paymentType !== 'all' && appointment.paymentType !== filters.paymentType) {
                return false;
            }

            return true;
        }).sort((a, b) => {
            const direction = filters.sortDirection === 'asc' ? 1 : -1;
            
            switch (filters.sortBy) {
                case 'date':
                    const dateA = new Date(`${a.date} ${a.time}`);
                    const dateB = new Date(`${b.date} ${b.time}`);
                    return (dateA.getTime() - dateB.getTime()) * direction;
                
                case 'name':
                    return a.userName.localeCompare(b.userName) * direction;
                
                case 'status':
                    return a.status.localeCompare(b.status) * direction;
                
                default:
                    return 0;
            }
        });
    }, [appointments, filters]);

    const handleViewDetails = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setDetailsDialogOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!collectionExists) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Randevular
                </Typography>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Randevu sistemi henüz başlatılmamış
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
            <Container maxWidth="xl">
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h4" gutterBottom>
                        Randevular
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <FormControl sx={{ minWidth: 200 }}>
                                        <InputLabel>Durum</InputLabel>
                                        <Select
                                            value={filters.status}
                                            label="Durum"
                                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                        >
                                            <MenuItem value="all">Tümü</MenuItem>
                                            <MenuItem value="pending">Ödeme Bekliyor</MenuItem>
                                            <MenuItem value="confirmed">Onaylandı</MenuItem>
                                            <MenuItem value="completed">Tamamlandı</MenuItem>
                                            <MenuItem value="rejected">Reddedildi</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ minWidth: 200 }}>
                                        <InputLabel>Ödeme Tipi</InputLabel>
                                        <Select
                                            value={filters.paymentType}
                                            label="Ödeme Tipi"
                                            onChange={(e) => setFilters({ ...filters, paymentType: e.target.value })}
                                        >
                                            <MenuItem value="all">Tümü</MenuItem>
                                            <MenuItem value="regular">Havale/EFT</MenuItem>
                                            <MenuItem value="points">Puan</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <DatePicker
                                        label="Başlangıç Tarihi"
                                        value={filters.startDate}
                                        onChange={(newValue) => setFilters({ ...filters, startDate: newValue })}
                                        slotProps={{ textField: { sx: { minWidth: 200 } } }}
                                    />

                                    <DatePicker
                                        label="Bitiş Tarihi"
                                        value={filters.endDate}
                                        onChange={(newValue) => setFilters({ ...filters, endDate: newValue })}
                                        slotProps={{ textField: { sx: { minWidth: 200 } } }}
                                    />

                                    <FormControl sx={{ minWidth: 200 }}>
                                        <InputLabel>Sıralama</InputLabel>
                                        <Select
                                            value={filters.sortBy}
                                            label="Sıralama"
                                            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                                        >
                                            <MenuItem value="date">Tarih ve Saat</MenuItem>
                                            <MenuItem value="name">İsim</MenuItem>
                                            <MenuItem value="status">Durum</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl sx={{ minWidth: 150 }}>
                                        <InputLabel>Sıralama Yönü</InputLabel>
                                        <Select
                                            value={filters.sortDirection}
                                            label="Sıralama Yönü"
                                            onChange={(e) => setFilters({ ...filters, sortDirection: e.target.value as 'asc' | 'desc' })}
                                        >
                                            <MenuItem value="asc">Artan</MenuItem>
                                            <MenuItem value="desc">Azalan</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tarih/Saat</TableCell>
                                    <TableCell>İsim</TableCell>
                                    <TableCell>İletişim</TableCell>
                                    <TableCell>Durum</TableCell>
                                    <TableCell>Ödeme Tipi</TableCell>
                                    <TableCell>Dekont</TableCell>
                                    <TableCell>İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAppointments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                {filters.status === 'all' && filters.paymentType === 'all'
                                                    ? 'Henüz randevu bulunmuyor'
                                                    : 'Seçilen filtrelere uygun randevu bulunamadı'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredAppointments.map((appointment) => (
                                        <TableRow key={appointment.id}>
                                            <TableCell>
                                                {format(parseISO(appointment.date), 'dd.MM.yyyy')}
                                                <br />
                                                {appointment.time}
                                            </TableCell>
                                            <TableCell>{appointment.userName || 'Belirtilmemiş'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {appointment.userEmail || 'E-posta belirtilmemiş'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {appointment.userPhone || 'Telefon belirtilmemiş'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={2}>
                                                    <Chip
                                                        label={getStatusText(appointment.status)}
                                                        color={getStatusChipColor(appointment.status)}
                                                        size="small"
                                                    />
                                                    {appointment.paymentType === 'points' && (
                                                        <Chip
                                                            label="Puan"
                                                            color="secondary"
                                                            size="small"
                                                        />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {appointment.paymentType === 'points' ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Puan
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Havale/EFT
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {appointment.receiptUrl ? (
                                                    <Link 
                                                        href={appointment.receiptUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        sx={{ display: 'flex', alignItems: 'center' }}
                                                    >
                                                        Görüntüle <OpenInNew sx={{ ml: 0.5, fontSize: 16 }} />
                                                    </Link>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Yüklenmedi
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    {appointment.status === 'pending' && (
                                                        <>
                                                            <Tooltip title="Onayla">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => handleStatusChange('confirmed', appointment.id)}
                                                                >
                                                                    <CheckIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reddet">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        setSelectedAppointmentId(appointment.id);
                                                                        setRejectDialogOpen(true);
                                                                    }}
                                                                >
                                                                    <CloseIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {appointment.status === 'confirmed' && (
                                                        <Tooltip title="Tamamlandı">
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleStatusChange('completed', appointment.id)}
                                                            >
                                                                <DoneIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleStatusMenuOpen(e, appointment)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Tooltip title="Detaylar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewDetails(appointment)}
                                                        >
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
                        <DialogTitle>Randevu Reddetme</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Red Nedeni"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setRejectDialogOpen(false)}>İptal</Button>
                            <Button onClick={handleReject} color="error">
                                Reddet
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleStatusMenuClose}
                    >
                        <MenuItem onClick={() => handleStatusChange('pending', selectedAppointment?.id)}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    label="Ödeme Bekliyor"
                                    color="warning"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                Ödeme Bekliyor olarak işaretle
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusChange('confirmed', selectedAppointment?.id)}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    label="Onaylandı"
                                    color="info"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                Onaylandı olarak işaretle
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusChange('completed', selectedAppointment?.id)}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    label="Tamamlandı"
                                    color="success"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                Tamamlandı olarak işaretle
                            </Box>
                        </MenuItem>
                    </Menu>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                    >
                        <Alert
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            severity={snackbar.severity}
                            sx={{ width: '100%' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>

                    <AppointmentDetailsDialog
                        open={detailsDialogOpen}
                        onClose={() => setDetailsDialogOpen(false)}
                        appointment={selectedAppointment}
                        spreadInfo={selectedAppointment ? spreads.get(selectedAppointment.spreadId) : undefined}
                    />
                </Box>
            </Container>
        </LocalizationProvider>
    );
};

export default AppointmentAdmin; 